// Contrats du socle IA (S1), communs à l'API et aux interfaces.
// Le registre ferme les usages et leurs bornes ; aucun add-on ne choisit un
// modèle hors des tarifs. Les coûts restent en micro-dollars US entiers,
// indépendamment de la devise utilisée pour les ventes de la galerie.
import { z } from 'zod';
export const ADDONS_IA = [
    'assistant-reponse',
    'catalogage-image',
    'studio-contenu',
    'assistant-recherche',
];
export const NIVEAUX_IA = {
    rapide: { modele: 'claude-haiku-5-5', effort: 'low' },
    redaction: { modele: 'claude-sonnet-5-5', effort: 'medium' },
    // Haiku avec plus d'effort que `rapide`, trop court pour choisir des outils.
    recherche: { modele: 'claude-haiku-5-5', effort: 'medium' },
};
export const niveauIaSchema = z.enum(Object.keys(NIVEAUX_IA));
export const USAGES_IA = {
    'voix:apercu': {
        addon: null,
        niveau: 'redaction',
        entreeMax: 8_000,
        sortieMax: 2_000,
        imagesMax: 0,
    },
    'reponse:tri': {
        addon: 'assistant-reponse',
        niveau: 'rapide',
        entreeMax: 12_000,
        sortieMax: 1_000,
        imagesMax: 0,
    },
    'reponse:brouillon': {
        addon: 'assistant-reponse',
        niveau: 'redaction',
        entreeMax: 40_000,
        sortieMax: 4_000,
        imagesMax: 0,
    },
    'reponse:relance': {
        addon: 'assistant-reponse',
        niveau: 'redaction',
        entreeMax: 20_000,
        sortieMax: 3_000,
        imagesMax: 0,
    },
    'reponse:pratique': {
        addon: 'assistant-reponse',
        niveau: 'rapide',
        entreeMax: 12_000,
        sortieMax: 1_500,
        imagesMax: 0,
    },
    'catalogage:recto': {
        addon: 'catalogage-image',
        niveau: 'redaction',
        entreeMax: 8_000,
        sortieMax: 4_000,
        imagesMax: 4,
    },
    'catalogage:verso': {
        addon: 'catalogage-image',
        niveau: 'redaction',
        entreeMax: 8_000,
        sortieMax: 4_000,
        imagesMax: 4,
    },
    'catalogage:biographie': {
        addon: 'catalogage-image',
        niveau: 'redaction',
        entreeMax: 20_000,
        sortieMax: 4_000,
        imagesMax: 0,
    },
    'studio:texte': {
        addon: 'studio-contenu',
        niveau: 'redaction',
        entreeMax: 40_000,
        sortieMax: 8_000,
        imagesMax: 0,
    },
    'studio:traduction': {
        addon: 'studio-contenu',
        niveau: 'redaction',
        entreeMax: 40_000,
        sortieMax: 12_000,
        imagesMax: 0,
    },
    'recherche:planifier': {
        addon: 'assistant-recherche',
        niveau: 'recherche',
        entreeMax: 8_000,
        sortieMax: 1_000,
        imagesMax: 0,
    },
    'recherche:repondre': {
        addon: 'assistant-recherche',
        niveau: 'recherche',
        entreeMax: 30_000,
        sortieMax: 1_500,
        imagesMax: 0,
    },
    'recherche:approfondir': {
        addon: 'assistant-recherche',
        niveau: 'redaction',
        entreeMax: 40_000,
        sortieMax: 2_500,
        imagesMax: 0,
    },
    'recherche:lire': {
        addon: 'assistant-recherche',
        niveau: 'recherche',
        entreeMax: 40_000,
        sortieMax: 1_500,
        imagesMax: 0,
    },
};
export const usageIaSchema = z.enum(Object.keys(USAGES_IA));
/** Micro-dollars par jeton : les tarifs évitent toute conversion de devise par appel. */
export const TARIFS_IA = {
    'claude-haiku-5-5': { entree: 0.1, sortie: 0.5, cacheLecture: 0.01, cacheEcriture: 0.125 },
    'claude-sonnet-5-5': { entree: 2, sortie: 10, cacheLecture: 0.2, cacheEcriture: 2.5 },
    'claude-opus-5-5': { entree: 4, sortie: 20, cacheLecture: 0.2, cacheEcriture: 5 },
};
export const TARIF_IA_INCONNU = { entree: 10, sortie: 50, cacheLecture: 1, cacheEcriture: 12.5 };
export const jetonsIaSchema = z.strictObject({
    entree: z.int().nonnegative(),
    sortie: z.int().nonnegative(),
    cacheLecture: z.int().nonnegative(),
    cacheEcriture: z.int().nonnegative(),
});
/** Les compteurs d'entrée Anthropic excluent les lectures et écritures de cache. */
export function coutMicro(modele, jetons) {
    const t = Object.hasOwn(TARIFS_IA, modele)
        ? TARIFS_IA[modele]
        : TARIF_IA_INCONNU;
    // La taille du contexte inclut le cache : un préfixe réutilisé ne retire pas
    // l'appel du palier long de Haiku.
    const long = modele === 'claude-haiku-5-5' &&
        jetons.entree + jetons.cacheLecture + jetons.cacheEcriture > 100_000;
    // Le palier long multiplie les prix de Haiku par cinq, cache compris.
    return Math.ceil(((jetons.entree * (t.entree * 1000) +
        jetons.sortie * (t.sortie * 1000) +
        jetons.cacheLecture * (t.cacheLecture * 1000) +
        jetons.cacheEcriture * (t.cacheEcriture * 1000)) *
        (long ? 5 : 1)) /
        1000);
}
export const ETATS_APPEL_IA = [
    'reussi',
    'refus',
    'invalide',
    'indisponible',
    'plafond',
    'coupee',
    'debit',
];
export const voixIaSchema = z.strictObject({
    ton: z.enum(['sobre', 'chaleureux', 'soutenu']).default('sobre'),
    adresse: z.enum(['vous', 'tu']).default('vous'),
    langues: z
        .array(z.enum(['fr', 'en']))
        .min(1)
        .max(2)
        .refine((v) => new Set(v).size === v.length)
        .default(['fr', 'en']),
    signature: z.string().max(500).default(''),
    consignes: z.string().max(2_000).default(''),
    sujetsExclus: z.array(z.string().max(80)).max(20).default([]),
    exemples: z
        .array(z.strictObject({ titre: z.string().max(120), texte: z.string().max(4_000) }))
        .max(5)
        .default([]),
});
export const modificationVoixIaSchema = voixIaSchema.extend({ version: z.int().nonnegative() });
const date = z.iso.datetime();
export const reponseVoixIaSchema = voixIaSchema.extend({
    version: z.int().nonnegative(),
    modifieLe: date.nullable(),
    modifiePar: z.string().nullable(),
});
export const apercuVoixIaSchema = z.strictObject({ question: z.string().trim().min(1).max(1_000) });
export const texteApercuIaSchema = z.strictObject({ texte: z.string().min(1).max(8_000) });
export const reponseApercuIaSchema = texteApercuIaSchema.extend({ appelId: z.string() });
export const consommationIaSchema = z.strictObject({
    mois: z.string().regex(/^\d{4}-\d{2}$/),
    depenseMicro: z.number().nonnegative(),
    depenseCents: z.number().nonnegative(),
    reserveMicro: z.number().nonnegative(),
    plafondMicro: z.number().nonnegative(),
    plafondCents: z.number().nonnegative(),
    partUtilisee: z.number().nonnegative(),
    seuil80Atteint: z.boolean(),
});
export const reponseEtatIaSchema = z.strictObject({
    disponibilite: z.enum(['active', 'coupee', 'suspendue', 'indisponible']),
    usagesPermis: z.array(usageIaSchema),
    consommation: consommationIaSchema,
});
export const appelIaSchema = z.strictObject({
    id: z.string(),
    usage: usageIaSchema,
    modele: z.string(),
    niveau: niveauIaSchema,
    etat: z.enum(ETATS_APPEL_IA),
    jetons: jetonsIaSchema,
    coutMicro: z.number().nonnegative(),
    coutCents: z.number().nonnegative(),
    dureeMs: z.number().nonnegative(),
    tentatives: z.int().nonnegative(),
    acteur: z.strictObject({ type: z.enum(['COMPTE', 'AGENT', 'SYSTEME']), id: z.string() }),
    objet: z.strictObject({ type: z.string(), id: z.string() }).nullable(),
    creeLe: date,
});
export const reponseAppelsIaSchema = z.strictObject({
    appels: z.array(appelIaSchema),
    page: z.int().positive(),
    parPage: z.literal(50),
    total: z.int().nonnegative(),
});
export const coupureIaSchema = z.strictObject({
    coupee: z.boolean(),
    usagesCoupes: z.array(usageIaSchema).max(10),
    motif: z.string().trim().min(1).max(500),
});
export const reglagesGalerieIaSchema = z.strictObject({
    plafondMensuelCents: z.int().min(0).max(100_000).nullable(),
    suspendue: z.boolean(),
    motif: z.string().trim().min(1).max(500),
});
const historiqueIaSchema = coupureIaSchema.extend({ auteur: z.string(), modifieLe: date });
export const reponseConsoleGalerieIaSchema = z.strictObject({
    plafondMensuelCents: z.number().nullable(),
    plafondEffectifCents: z.number(),
    suspendue: z.boolean(),
    motif: z.string(),
    consommations: z.array(z.strictObject({
        mois: z.string(),
        depenseMicro: z.number(),
        depenseCents: z.number(),
        reserveMicro: z.number(),
        appels: z.number(),
        refus: z.number(),
        jetonsEntree: z.number(),
        jetonsSortie: z.number(),
        parUsage: z.record(z.string(), z.strictObject({ appels: z.number(), depenseMicro: z.number(), depenseCents: z.number() })),
    })),
});
export const reponseConsoleIaSchema = z.strictObject({
    lisibles: z.boolean(),
    coupee: z.boolean(),
    secours: z.boolean(),
    usagesCoupes: z.array(usageIaSchema),
    historique: z.array(historiqueIaSchema),
    mois: z.string(),
    depenseMicro: z.number(),
    depenseCents: z.number(),
    premieresGaleries: z.array(z.strictObject({ galleryId: z.string(), depenseMicro: z.number(), depenseCents: z.number() })),
    galeriesSeuil80: z.array(z.string()),
});
//# sourceMappingURL=ia.js.map